import { webvideo } from "@scrile/api-provider/dist";
import Configuration from "@scrile/api-provider/dist/Configuration";
import config from "config";
import emitter from "./emitter";

const result = webvideo({
  url: config.apiUrl,
  notificationCB: (event, payload) => {
    emitter.emit(event, payload);
  },
  appStorage: {
    getString(key: string, defaultValue: string): string | Promise<string> {
      return localStorage.getItem(key) || defaultValue;
    },
    setString(key: string, value: string): void | Promise<void> {
      localStorage.setItem(key, value);
    },
  },
  websocketOnly: true,
});

Configuration.featureLiveChat = true;

const providers = result.providers;

// Coalesced wait for the in-memory short-live token: every preview/poster on a
// freshly-loaded page calls getAuthToken() at once, so they share one poll.
let shortTokenWait: Promise<string> | null = null;

/**
 * Poll the in-memory short-live token until it appears (or `timeoutMs`).
 *
 * In websocketOnly mode the short-live (ACCESS) token isn't fetched over HTTP —
 * it's pushed by the GraphQL WebSocket a beat AFTER the app mounts. There's no
 * synchronous refresh path to force it, so we wait for the WS to deliver it.
 */
async function waitForShortLiveToken(api: any, timeoutMs: number): Promise<string> {
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const token = await api.tokens.getShortLiveToken();
    if (token) return token;
    if (Date.now() - start >= timeoutMs) return "";
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
}

/**
 * Get the current auth token (JWT) for authenticated file fetches (recording
 * video/poster/transcript URLs that carry it as `?token=`).
 *
 * ONLY the short-live ACCESS token works here: the recording file route
 * (graphql `helpers/requireAuth.js`) derives the caller's userId from the
 * token's `sub` claim. The long-live token is REFRESH-audience (`operatorId`
 * only, no `sub`) — attaching it yields userId 0 and a guaranteed 401, so we
 * must NOT fall back to it. On a fresh load the short-live token arrives over
 * the WS a moment after mount, so we briefly wait for it; if it never comes we
 * return "" and let the SESSIONID cookie path (synced by App.tsx) handle auth.
 */
export async function getAuthToken(): Promise<string> {
  try {
    const api = (providers.MessagesProvider as any).api;
    if (api && api.tokens) {
      let shortToken = await api.tokens.getShortLiveToken();
      if (!shortToken) {
        if (!shortTokenWait) {
          shortTokenWait = waitForShortLiveToken(api, 8000).finally(() => {
            shortTokenWait = null;
          });
        }
        shortToken = await shortTokenWait;
      }
      if (shortToken) return shortToken;
    }
  } catch { /* ignore */ }
  return "";
}

export default providers;
